import titles from "../components/titles";

const preparationLabels = {
  no_preparation: "No special preparation",
  special_diet: "Special diet",
  food_restriction: "Food restriction",
  water_restriction: "Water restriction",
  antibiotics_treatment: "Antibiotics treatment in water",
  tamoxifen_treatment: "Tamoxifen treatment in food/gels",
  other_preparation: "Other special preparation",
};

const formatPreparationLabel = (value) => preparationLabels[value] || "Special preparation";

export const getInterventionTypeLabel = (intervention) => {
  if (!intervention) return "Intervention";
  if (intervention.interventionType === "Other" && intervention.interventionTypeOther?.trim()) {
    return intervention.interventionTypeOther.trim();
  }
  return intervention.interventionType || "Intervention";
};

export const getInterventionMethodLabel = (intervention) => {
  if (!intervention) return "";
  const method = intervention.interventionMethod || "";
  if (method.toLowerCase().includes("other") && intervention.interventionMethodOther?.trim()) {
    return intervention.interventionMethodOther.trim();
  }
  return method;
};

export const getInterventionOccurrences = (intervention) => {
  const repeatType = intervention?.repeatedIntervention;

  if (repeatType === "specific_days" && intervention?.specificDays) {
    return intervention.specificDays
      .split(",")
      .map((d) => parseInt(d.trim(), 10))
      .filter((d) => !isNaN(d) && d > 0)
      .sort((a, b) => a - b);
  }

  const startDay = parseInt(intervention?.daysStart, 10);
  if (Number.isNaN(startDay)) return [];

  const xDays = parseInt(intervention?.xDays, 10);
  const yTimes = parseInt(intervention?.yTimes, 10);

  if (repeatType === "every_day" && !Number.isNaN(xDays) && xDays > 0) {
    return Array.from({ length: xDays }, (_, idx) => startDay + idx);
  }

  if (repeatType === "every_x_days" && !Number.isNaN(xDays) && !Number.isNaN(yTimes) && xDays > 0 && yTimes > 0) {
    return Array.from({ length: yTimes }, (_, idx) => startDay + idx * xDays);
  }

  if (repeatType === "after_x_days" && !Number.isNaN(xDays) && xDays > 0) {
    return [startDay, startDay + xDays];
  }

  return [startDay];
};

export const getTimelineLegendEntries = (experiment) => {
  const legendMap = new Map();

  if (experiment?.acclimatation === "yes" && parseInt(experiment?.acclimatationDays || "0", 10) > 0) {
    legendMap.set("Acclimatation", { label: "Acclimatation", icon: null });
  }

  if (experiment?.preparation && experiment.preparation !== "no_preparation" && parseInt(experiment?.preparationDays || "0", 10) > 0) {
    legendMap.set("Special preparation", { label: "Special preparation", icon: "/icons/SpecialPreparation.svg" });
  }

  (experiment?.interventions || []).forEach((intervention) => {
    const typeLabel = getInterventionTypeLabel(intervention);
    const titleMatch = titles.find((entry) => entry.label === intervention.interventionType);
    const icon = titleMatch?.icon || "/icons/PointInterrogation.svg";
    if (!legendMap.has(typeLabel)) {
      legendMap.set(typeLabel, { label: typeLabel, icon });
    }
  });

  return Array.from(legendMap.values());
};

const buildScheduleLabel = (intervention, occurrences) => {
  if (occurrences.length === 0) return "Not scheduled";
  if (intervention?.repeatedIntervention === "specific_days") {
    return `On days: ${occurrences.join(", ")}`;
  }
  if (intervention?.repeatedIntervention === "every_day" && intervention?.xDays) {
    return `Daily for ${intervention.xDays} days, starting on day ${occurrences[0]}`;
  }
  if (intervention?.repeatedIntervention === "every_x_days" && intervention?.xDays && intervention?.yTimes) {
    return `Every ${intervention.xDays} days for ${intervention.yTimes} occurrences, starting on day ${occurrences[0]}`;
  }
  if (intervention?.repeatedIntervention === "after_x_days" && intervention?.xDays) {
    return `Day ${occurrences[0]} followed by a repeat after ${intervention.xDays} days`;
  }
  return `Single intervention on day ${occurrences[0]}`;
};

export const buildExperimentProcedureEntries = (experiment) =>
  (experiment?.interventions || []).map((intervention, index) => {
    const occurrences = getInterventionOccurrences(intervention);
    return {
      index,
      typeLabel: getInterventionTypeLabel(intervention),
      methodLabel: getInterventionMethodLabel(intervention) || "Not specified",
      occurrences,
      dayLabel: occurrences.length > 0 ? occurrences.map((day) => `Day ${day}`).join(", ") : "Not scheduled",
      scheduleLabel: buildScheduleLabel(intervention, occurrences),
    };
  });

export const buildExperimentTimelineOverview = (experiment, index = 0) => {
  const acclimatationDays = parseInt(experiment?.acclimatationDays || "0", 10) || 0;
  const preparationDays = parseInt(experiment?.preparationDays || "0", 10) || 0;
  const durationDays = parseInt(experiment?.experimentDuration || "0", 10) || 0;
  const cohorts = experiment?.cohorts?.trim() || "";
  const repetitions = experiment?.repetitions?.trim() || "";
  const keySteps = [];

  if (acclimatationDays > 0) {
    keySteps.push({
      day: 1,
      label: `Acclimatation period (${acclimatationDays} days)`,
      category: "Preparation",
    });
  }

  if (preparationDays > 0 && experiment?.preparation && experiment.preparation !== "no_preparation") {
    const prepStart = acclimatationDays > 0 ? acclimatationDays + 1 : 1;
    keySteps.push({
      day: prepStart,
      label: `${formatPreparationLabel(experiment.preparation)} (${preparationDays} days)`,
      category: "Preparation",
    });
  }

  (experiment?.interventions || []).forEach((intervention, interventionIndex) => {
    const occurrences = getInterventionOccurrences(intervention);
    const typeLabel = getInterventionTypeLabel(intervention);
    const methodLabel = getInterventionMethodLabel(intervention);
    const summary = methodLabel ? `${typeLabel} (${methodLabel})` : typeLabel;

    occurrences.forEach((day, occurrenceIndex) => {
      const suffix = occurrences.length > 1 ? `, occurrence ${occurrenceIndex + 1}` : "";
      keySteps.push({
        day,
        label: `${summary}${suffix}`,
        category: "Intervention",
        interventionIndex,
      });
    });
  });

  keySteps.sort((a, b) => a.day - b.day || a.label.localeCompare(b.label));

  return {
    experimentLabel: experiment?.experimentObjective || `Experiment ${index + 1}`,
    objectiveLabel: experiment?.objectiveLabel || `Objective ${index + 1}`,
    durationDays,
    acclimatationDays,
    preparationDays,
    preparationLabel: formatPreparationLabel(experiment?.preparation),
    cohorts,
    repetitions,
    legendEntries: getTimelineLegendEntries(experiment),
    keySteps,
    procedureEntries: buildExperimentProcedureEntries(experiment),
  };
};
